/**
 * Diff — slotting-core
 * Pure comparison of instance maps (current vs proposed slotting
 * configuration), used by the editor's visual diff and the dry-run summary.
 */

import type { InstanceMap } from './types.js';

export interface InstancePoolDiffEntry {
  instanceType: string;
  currentCount: number;
  proposedCount: number;
  /** proposedCount - currentCount */
  delta: number;
}

/**
 * Compare two instance maps. Returns one entry per instance type present in
 * either map (with a count > 0 on at least one side), sorted by type name.
 */
export function diffInstanceMaps(
  current: InstanceMap,
  proposed: InstanceMap,
): InstancePoolDiffEntry[] {
  const types = new Set([...Object.keys(current), ...Object.keys(proposed)]);
  return [...types]
    .sort()
    .map((instanceType) => {
      const currentCount = current[instanceType] ?? 0;
      const proposedCount = proposed[instanceType] ?? 0;
      return { instanceType, currentCount, proposedCount, delta: proposedCount - currentCount };
    })
    .filter((entry) => entry.currentCount > 0 || entry.proposedCount > 0);
}

/** True when at least one entry changed. */
export function hasChanges(diff: InstancePoolDiffEntry[]): boolean {
  return diff.some((entry) => entry.delta !== 0);
}
